import { Shield, Target, Users, Award, Heart, TrendingUp } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";

export const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Protection First",
      description: "Your security and peace of mind are our top priorities in every policy we recommend",
    },
    {
      icon: Users,
      title: "Client-Centered",
      description: "We build lasting relationships by putting your needs and goals at the center of everything we do",
    },
    {
      icon: Heart,
      title: "Integrity Always",
      description: "Honest advice and transparent communication are the foundations of our service",
    },
    {
      icon: Award,
      title: "Excellence Driven",
      description: "We continuously strive to exceed expectations and deliver outstanding insurance solutions",
    },
  ];

  const teamHighlights = [
    {
      icon: Target,
      stat: "50+",
      label: "Years of Combined Experience",
    },
    {
      icon: Users,
      stat: "1000+",
      label: "Satisfied Clients",
    },
    {
      icon: TrendingUp,
      stat: "95%",
      label: "Client Retention Rate",
    },
  ];

  return (
    <div>
      {/* Hero Section with Background Image */}
      <section className="relative bg-gradient-to-br from-primary to-blue-700 text-white section-padding overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/6950093/pexels-photo-6950093.jpeg?w=1600&q=80')",
          }}
        ></div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">About Henry T. Warner Co.</h1>
            <p className="text-xl text-blue-100">
              Independent insurance professionals dedicated to protecting what matters most to you and your business
            </p>
          </div>
        </div>
      </section>

      {/* Our Story with Image */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">Our Story</h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  Henry T. Warner Co. is a trusted independent insurance agency serving communities across seven states: Minnesota, Wisconsin, Iowa, North Dakota, South Dakota, Tennessee, and Arizona.
                </p>
                <p>
                  As an independent agency, we have the unique advantage of working with multiple insurance carriers. This means we work for you, not the insurance companies. Our mission is to find the best coverage options that meet your specific needs and budget.
                </p>
                <p>
                  Whether you're looking for personal insurance to protect your family and assets, or commercial insurance to safeguard your business, we have the expertise and carrier relationships to deliver comprehensive solutions.
                </p>
                <p>
                  Our team of experienced insurance professionals is committed to providing personalized service, expert guidance, and reliable support throughout your insurance journey.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg?w=800&q=80"
                  alt="Business partnership handshake"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-8 max-w-[280px]">
                <div className="space-y-4">
                  {teamHighlights.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="bg-primary/10 p-2 rounded-lg">
                        <item.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-primary">{item.stat}</div>
                        <div className="text-xs text-muted-foreground">{item.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Our Core Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide every decision we make and every policy we recommend
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="card-hover border-2 hover:border-primary">
                <CardContent className="pt-6 text-center">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
              What Makes Us Different
            </h2>
            <div className="space-y-8">
              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-semibold mb-3 text-foreground">Independent Advantage</h3>
                  <p className="text-lg text-muted-foreground">
                    Unlike captive agents who work for a single insurance company, we represent multiple carriers. This independence allows us to shop the market on your behalf and present you with the best options available.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-semibold mb-3 text-foreground">Multi-State Expertise</h3>
                  <p className="text-lg text-muted-foreground">
                    With coverage across seven states, we understand the unique insurance requirements, regulations, and challenges specific to each region we serve. Our broad geographic reach is matched by deep local knowledge.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-semibold mb-3 text-foreground">Personalized Service</h3>
                  <p className="text-lg text-muted-foreground">
                    We believe insurance is personal. That's why we take the time to understand your unique situation, answer your questions, and provide ongoing support. You're not just a policy number—you're part of our family.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-semibold mb-3 text-foreground">Comprehensive Solutions</h3>
                  <p className="text-lg text-muted-foreground">
                    Whether you need personal insurance for your home and vehicles or commercial coverage for your business, we offer a full suite of insurance products to protect what matters most to you.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">Proudly Serving Seven States</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            We're honored to provide insurance solutions to individuals and businesses across the Upper Midwest, Great Plains, and beyond
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {["Minnesota", "Wisconsin", "Iowa", "North Dakota", "South Dakota", "Tennessee", "Arizona"].map((state) => (
              <div
                key={state}
                className="bg-white px-8 py-4 rounded-lg shadow-md hover:shadow-lg transition-shadow border-2 border-primary/20 hover:border-primary"
              >
                <span className="text-xl font-semibold text-foreground">{state}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
